import { useState } from 'react'

import {
  Alert,
  Box,
  Form,
  Link,
  Text,
  ToggleButton,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'

import DayPicker from 'modules/core/components/DayPicker'
import TimePicker from 'modules/core/components/TimePicker'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { useAttendanceSettingWorkCategories } from 'modules/attendance/lib/attendanceSettingWorkCategoriesContext'
import {
  WizardData,
  WizardDataField,
  validateMandatoryFields,
} from 'modules/attendance/lib/forms/utils/CustomTimeRangesFormData'
import TimeCategoriesUtils from 'modules/attendance/lib/utils/TimeCategoriesUtils'

import CategorySelector from './CategorySelector'
import DaysSelector from './DaysSelector'

const getMandatoryFields = (data: WizardData): WizardDataField[] => {
  const base: WizardDataField[] = ['timeSettingsCustomTimeRangeCategoryId']

  const applyOnFields: WizardDataField[] = data.day ? ['day'] : ['date']
  const initialFields = base.concat(applyOnFields)

  return data.slotSelected === 'specific-time-slot'
    ? initialFields.concat(['startTime', 'endTime'])
    : initialFields
}

type Daily = 'day' | 'date'

const BasicDetailsStep = ({ data, onDataChanged }: WizardStepRenderParams<WizardData>) => {
  const { timeSettingsCategories } = useAttendanceSettingWorkCategories()
  const specialHourCategories = TimeCategoriesUtils.specialHoursCategories(timeSettingsCategories)
  const flexibleCategories = TimeCategoriesUtils.flexibleCategories(specialHourCategories)
  const { navigation } = useNavigation()
  const route = navigation.settings.time_categories_settings.fullPath()
  const isEdit = data.isEdit

  const [isSubmitted, setIsSubmitted] = useState(false)
  const [dailySelected, setDailySelected] = useState<Daily>(data.day ? 'day' : 'date')

  // TEMPORAL, until we migrate regular & overtime categories to flexible.
  // Normally, only flexible categories should be shown in the dropdown.
  // However, when editing special hours with regular or overtime categories, show this category as the selected option if applicable.
  let dropdownCategories = flexibleCategories

  if (
    isEdit &&
    data.timeSettingsCustomTimeRangeCategoryId &&
    !TimeCategoriesUtils.findCategoryById(
      flexibleCategories,
      data.timeSettingsCustomTimeRangeCategoryId
    )
  ) {
    const selectedCategory = TimeCategoriesUtils.findCategoryById(
      timeSettingsCategories,
      data.timeSettingsCustomTimeRangeCategoryId
    )

    if (selectedCategory) {
      dropdownCategories = [...flexibleCategories, selectedCategory]
    }
  }

  const handleNext = async () => {
    setIsSubmitted(true)

    const mandatoryFields = getMandatoryFields(data)
    const { invalidFields, dataWithErrors } = validateMandatoryFields(mandatoryFields, data)

    onDataChanged(dataWithErrors)

    track(trackingEvents.timeTracking.newSpecialHourStep1NextPressed, {
      errors: invalidFields.length ? JSON.stringify(invalidFields) : undefined,
      timeSettingsSelectedCategoryId: data.timeSettingsCustomTimeRangeCategoryId,
      days: JSON.stringify(data.day),
      date: data.date,
      slotSelected: data.slotSelected,
      startTime: data.startTime,
      endTime: data.endTime,
      isEdit: data.isEdit,
      id: data.id,
    })

    if (invalidFields.length) {
      return Promise.reject()
    }
  }
  useOnNext(handleNext)

  const handleDataChange = (newData: Partial<WizardData>) => {
    const updatedData = { ...data, ...newData }

    if (isSubmitted) {
      const mandatoryFields = getMandatoryFields(updatedData)
      const { dataWithErrors } = validateMandatoryFields(mandatoryFields, updatedData)
      onDataChanged(dataWithErrors)
    } else {
      onDataChanged(updatedData)
    }
  }

  return (
    <Box flexDirection="row" gap="s48">
      <Box flexDirection="column" gap="s24" width="s400">
        <Box gap="s8">
          <Text.Header2 weight="semibold">
            {i18n.t(
              'time_tracking.settings.time_blocks.categories.special_hours.wizard.basic_information.title'
            )}
          </Text.Header2>
          <Text.Normal>
            {i18n.t(
              'time_tracking.settings.time_blocks.categories.special_hours.wizard.basic_information.description'
            )}
          </Text.Normal>
        </Box>
        <Box flexDirection="row" gap="s16">
          <Form padding="s0" notRounded>
            <CategorySelector
              label={i18n.t(
                'time_tracking.settings.time_blocks.categories.special_hours.wizard.basic_information.name'
              )}
              timeSettingsCategories={TimeCategoriesUtils.orderByName(dropdownCategories)}
              onChange={(value) =>
                handleDataChange({ timeSettingsCustomTimeRangeCategoryId: value })
              }
              value={data.timeSettingsCustomTimeRangeCategoryId}
              error={isSubmitted ? data.errors?.timeSettingsCustomTimeRangeCategoryId : undefined}
              canCreateSettingCategory={data.canCreateSettingCategory}
              categoryType="special_hour"
              placeholder={
                TimeCategoriesUtils.hasCategories(dropdownCategories)
                  ? i18n.t(
                      'time_tracking.settings.time_blocks.categories.special_hours.wizard.basic_information.select_or_create.not_empty'
                    )
                  : i18n.t(
                      'time_tracking.settings.time_blocks.categories.special_hours.wizard.basic_information.select_or_create.empty'
                    )
              }
            />
            {
              <Box gap="s8">
                <Text.Normal size="200" weight="medium" color="grey1000">
                  {i18n.t(
                    'time_tracking.settings.time_blocks.categories.special_hours.wizard.basic_information.apply_on'
                  )}
                </Text.Normal>
                <Box flexDirection="row" gap="s8" paddingTop="s8">
                  <ToggleButton
                    disabled={isEdit}
                    toggled={!!data.day}
                    label={i18n.t(
                      'time_tracking.settings.time_blocks.categories.special_hours.wizard.basic_information.apply_on_days'
                    )}
                    onClick={() => {
                      setDailySelected('day')
                      handleDataChange({
                        date: undefined,
                        day: [],
                      })
                    }}
                  ></ToggleButton>
                  <ToggleButton
                    disabled={isEdit}
                    toggled={dailySelected === 'date' || !!data.date}
                    label={i18n.t(
                      'time_tracking.settings.time_blocks.categories.special_hours.wizard.basic_information.apply_on_specific_date'
                    )}
                    onClick={() => {
                      setDailySelected('date')
                      handleDataChange({
                        day: undefined,
                      })
                    }}
                  ></ToggleButton>
                </Box>
              </Box>
            }

            {dailySelected === 'day' && data.day && (
              <DaysSelector
                onChange={(value) => value && handleDataChange({ day: value })}
                isEdit={isEdit}
                daysSelected={data.day}
                error={isSubmitted ? data.errors?.day : undefined}
              />
            )}
            {dailySelected === 'date' && (
              <>
                <DayPicker
                  placeholder={i18n.t(
                    'time_tracking.settings.time_blocks.categories.special_hours.wizard.basic_information.apply_on_specific_date_input_placeholder'
                  )}
                  value={data.date}
                  onChange={(value) => handleDataChange({ date: value })}
                  label={i18n.t(
                    'time_tracking.settings.time_blocks.categories.special_hours.wizard.basic_information.apply_on_specific_date_form_label'
                  )}
                  valueFormat="backend"
                  error={isSubmitted ? data.errors?.date : undefined}
                  mandatory
                  readonly={isEdit}
                />
              </>
            )}
            <Box gap="s8">
              <Text.Normal size="200" weight="medium" color="grey1000">
                {i18n.t('time_tracking.settings.time_blocks.categories.modals.apply_to')}
              </Text.Normal>
              <Box flexDirection="row" gap="s16">
                <ToggleButton
                  toggled={data.slotSelected === 'specific-time-slot'}
                  onClick={() => handleDataChange({ slotSelected: 'specific-time-slot' })}
                  label={i18n.t(
                    'time_tracking.settings.time_blocks.categories.special_hours.wizard.basic_information.time_slot'
                  )}
                />
                <ToggleButton
                  toggled={data.slotSelected === 'full-day'}
                  onClick={() =>
                    handleDataChange({
                      slotSelected: 'full-day',
                      startTime: undefined,
                      endTime: undefined,
                    })
                  }
                  label={i18n.t('time_tracking.settings.time_blocks.categories.modals.full_day')}
                />
              </Box>
            </Box>
            {data.slotSelected === 'specific-time-slot' && (
              <Box flexDirection="row" gap="s16">
                <TimePicker
                  label={i18n.t(
                    'time_tracking.settings.custom_time_range_section.modals.start_time'
                  )}
                  onChange={(value: string) => handleDataChange({ startTime: value })}
                  value={data.startTime}
                  mandatory
                  error={isSubmitted ? data.errors?.startTime : undefined}
                />
                <TimePicker
                  label={i18n.t('time_tracking.settings.custom_time_range_section.modals.end_time')}
                  onChange={(value: string) => handleDataChange({ endTime: value })}
                  value={data.endTime}
                  mandatory
                  error={isSubmitted ? data.errors?.endTime : undefined}
                />
              </Box>
            )}
          </Form>
        </Box>
      </Box>
      <Box
        justifyContent="center"
        maxWidth="s200"
        onClick={() => track(trackingEvents.timeTracking.goToWorkTimeCategoriesSettingsLinkPressed)}
      >
        {isEdit && (
          <Alert color="info">
            <Text.Normal>
              {i18n.t(
                'time_tracking.settings.time_blocks.categories.special_hours.wizard.basic_information.edit_categories_hint'
              )}
              <Link.Href href={route} target="_blank">
                {i18n.t(
                  'time_tracking.settings.time_blocks.categories.special_hours.wizard.basic_information.edit_categories_hint_link'
                )}
              </Link.Href>
            </Text.Normal>
          </Alert>
        )}
      </Box>
    </Box>
  )
}

export const basicDetailsStep = (): WizardStep<WizardData> => ({
  title: i18n.t(
    'time_tracking.settings.time_blocks.categories.special_hours.wizard.basic_information.title'
  ),
  nextText: i18n.t('time_tracking.settings.time_blocks.categories.special_hours.wizard.next'),
  render: (props) => <BasicDetailsStep {...props} />,
  isCompleted: () => true,
})
